import { useCallback, useEffect, useState } from 'react';
import { fetchMemberCeilings, type MemberCeiling, type SignedRosterReadSdk } from '@/lib/clients/vault/orgs';

export interface MemberCeilingsState {
  ceilings: Record<string, MemberCeiling> | null;
  loading: boolean;
  error: string | null;
  /**
   * Re-read the signed roster for the CURRENT selection — the post-write
   * refresh for the ceiling picker (a committed ceiling write re-reads the
   * map through this rather than adding parallel fetch state).
   */
  reload: () => void;
}

/** The effect-owned half; `reload` is appended to the returned value. */
interface CeilingsData {
  ceilings: Record<string, MemberCeiling> | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: CeilingsData = { ceilings: null, loading: false, error: null };

/**
 * The selected vault context's member ceilings, read from the SIGNED ROSTER
 * over the BEARER client (`signedRoster` never signs, so an effect may own
 * it — the same contract `useOrgDetail` runs under), with stale-response
 * cancellation. `enabled` is false for a caller who can never see the picker
 * (a plain member): their selection must not pay a signed-roster GET whose
 * values nothing renders.
 *
 * A failed read leaves `ceilings` null and the roster untouched: every row
 * then displays Unknown rather than "none signed" (absence of a READ is not
 * absence of a ceiling), and the picker still works — the write never reads
 * this map.
 */
export function useMemberCeilings(
  readSdk: SignedRosterReadSdk,
  vaultId: string | null,
  enabled: boolean,
): MemberCeilingsState {
  const [state, setState] = useState<CeilingsData>(EMPTY);
  // Bumped by `reload` only; the effect cannot distinguish it from a
  // selection change, which is the point — a re-read IS a re-read.
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (vaultId === null || !enabled) {
      // Identity-check so the common case (already empty) does not re-render.
      setState((s) => (s === EMPTY ? s : EMPTY));
      return;
    }
    let cancelled = false;
    setState({ ceilings: null, loading: true, error: null });
    void (async () => {
      try {
        const ceilings = await fetchMemberCeilings(readSdk, vaultId);
        if (!cancelled) setState({ ceilings, loading: false, error: null });
      } catch (e) {
        if (!cancelled) {
          setState({ ceilings: null, loading: false, error: e instanceof Error ? e.message : String(e) });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [readSdk, vaultId, enabled, revision]);

  const reload = useCallback(() => { setRevision((r) => r + 1); }, []);

  return { ...state, reload };
}
